import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import { AIAskSettings, DEFAULT_AI_ASK_SETTINGS } from '../services/aiAsk';

interface AIAskSettingsModalProps {
  initialSettings: AIAskSettings;
  onClose: () => void;
  onSave: (settings: AIAskSettings) => void;
  isSignedIn: boolean;
}

const AIAskSettingsModal: React.FC<AIAskSettingsModalProps> = ({
  initialSettings,
  onClose,
  onSave,
  isSignedIn,
}) => {
  const [usePersonalKey, setUsePersonalKey] = useState(initialSettings.usePersonalKey);
  const [baseUrl, setBaseUrl] = useState(initialSettings.baseUrl);
  const [apiKey, setApiKey] = useState(initialSettings.apiKey);
  const [token, setToken] = useState(initialSettings.token);
  const [model, setModel] = useState(initialSettings.model);

  useEffect(() => {
    setUsePersonalKey(initialSettings.usePersonalKey);
    setBaseUrl(initialSettings.baseUrl);
    setApiKey(initialSettings.apiKey);
    setToken(initialSettings.token);
    setModel(initialSettings.model);
  }, [initialSettings]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const normalizedModel = model.trim() || DEFAULT_AI_ASK_SETTINGS.model;
    const normalizedBaseUrl = baseUrl.trim();
    const normalizedKey = apiKey.trim();
    const normalizedToken = token.trim();

    if (usePersonalKey && !normalizedBaseUrl && !normalizedKey && !normalizedToken) {
      alert('Enter Base URL, token, or API key');
      return;
    }

    onSave({
      usePersonalKey,
      baseUrl: normalizedBaseUrl,
      apiKey: normalizedKey,
      token: normalizedToken,
      model: normalizedModel,
    });
    onClose();
  };

  return (
    <Modal onClose={onClose} maxWidth="max-w-md">
      <div className="p-6 md:p-8">
        <h2 className="text-2xl font-bold mb-2 text-light">AI Ask Settings</h2>
        <p className="text-sm text-medium mb-6">
          Stored only in this browser.
          {isSignedIn ? ' Kept separately for the signed-in user on this device.' : ' Saved for the guest profile on this device.'}
        </p>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <label className="flex items-start gap-3 rounded-xl bg-white/60 p-3">
              <input
                type="checkbox"
                checked={usePersonalKey}
                onChange={(event) => setUsePersonalKey(event.target.checked)}
                className="mt-1"
              />
              <div>
                <div className="font-semibold text-light">Use custom API settings</div>
                <p className="text-sm text-medium">When off, AI Ask uses the server default connection.</p>
              </div>
            </label>

            <div>
              <label htmlFor="ai-ask-base-url" className="block text-sm font-medium text-medium mb-1">
                Base URL
              </label>
              <input
                id="ai-ask-base-url"
                name="ai-ask-base-url"
                type="text"
                value={baseUrl}
                onChange={(event) => setBaseUrl(event.target.value)}
                className="w-full bg-white/70 border-slate-300 text-light rounded-md p-2 focus:ring-accent focus:border-accent"
                placeholder="http://172.30.1.92:18789"
                autoFocus
              />
              <p className="mt-1 text-xs text-medium">
                You can paste the host, /v1, or /v1/models. AI Ask normalizes it.
              </p>
            </div>

            <div>
              <label htmlFor="ai-ask-token" className="block text-sm font-medium text-medium mb-1">
                Token (optional)
              </label>
              <input
                id="ai-ask-token"
                name="ai-ask-token"
                type="password"
                value={token}
                onChange={(event) => setToken(event.target.value)}
                className="w-full bg-white/70 border-slate-300 text-light rounded-md p-2 focus:ring-accent focus:border-accent"
                placeholder="5600bc38..."
              />
            </div>

            <div>
              <label htmlFor="ai-ask-api-key" className="block text-sm font-medium text-medium mb-1">
                API Key (optional)
              </label>
              <input
                id="ai-ask-api-key"
                name="ai-ask-api-key"
                type="password"
                value={apiKey}
                onChange={(event) => setApiKey(event.target.value)}
                className="w-full bg-white/70 border-slate-300 text-light rounded-md p-2 focus:ring-accent focus:border-accent"
                placeholder="sk-..."
              />
            </div>

            <div>
              <label htmlFor="ai-ask-model" className="block text-sm font-medium text-medium mb-1">
                Model
              </label>
              <input
                id="ai-ask-model"
                name="ai-ask-model"
                type="text"
                value={model}
                onChange={(event) => setModel(event.target.value)}
                className="w-full bg-white/70 border-slate-300 text-light rounded-md p-2 focus:ring-accent focus:border-accent"
                placeholder={DEFAULT_AI_ASK_SETTINGS.model}
              />
            </div>
          </div>

          <div className="mt-8 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-light font-bold rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-accent hover:bg-fuchsia-600 text-white font-bold rounded-lg transition-colors"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AIAskSettingsModal;
